import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Split, Calculator } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SplitHistoryCard — Round 97l (Feb 2026).
 *
 * Lives on the Insights tab of the Stock Modal. Two things in one card:
 *   1. SPLIT HISTORY — every forward / reverse split / stock dividend
 *      event Massive reports for the ticker (sorted newest first).
 *   2. COST-BASIS CALCULATOR — paste an old purchase date + old share
 *      count, get back today's split-adjusted equivalent. Useful for
 *      long-term holders trying to reconcile decade-old broker
 *      statements with current positions.
 *
 * Paywall gating happens on the backend (`/api/stocks/:t/splits`
 * returns 402 for non-admin users). The card self-hides on 402 so
 * dividend-only users never see it. Self-hides on empty data so
 * tickers that never split (most ETFs, REITs, baby bonds) don't
 * waste vertical space.
 *
 * The math: Massive's `historical_adjustment_factor` is the cumulative
 * factor that converts an OLD PRICE to today's basis (e.g. AAPL's
 * 2005 entry shows 0.017857 — meaning a pre-2005 price multiplied by
 * 0.017857 yields today's basis price after the cumulative 56:1
 * forward splits AAPL has taken since). For SHARES the inverse
 * applies: adjusted_shares = raw_shares / factor (because forward
 * splits multiply share count while dividing price). Reverse splits
 * push the factor above 1.0 so the same formula works for both
 * directions automatically.
 */
function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
}

function fmtShares(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toFixed(2);
}

export default function SplitHistoryCard({ ticker }) {
  const [splits, setSplits] = useState(null);  // null = not loaded, [] = empty, [...] = data
  const [denied, setDenied] = useState(false);
  const [oldShares, setOldShares] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  // Round 96aa (Jul 2026) — post-split 90-day momentum callout.
  const [momentum, setMomentum] = useState(null);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setSplits(null);
    setDenied(false);
    setMomentum(null);
    axios.get(`${API}/stocks/${ticker}/splits`, { withCredentials: true })
      .then((r) => { if (!cancelled) setSplits(r.data?.splits || []); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 402) setDenied(true);
        else setSplits([]);
      });
    // Fire the momentum call in parallel. Failures are silent — the
    // main card still renders without the callout.
    axios.get(`${API}/stocks/${ticker}/splits/post-split-momentum`, { withCredentials: true })
      .then((r) => { if (!cancelled) setMomentum(r.data || null); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [ticker]);

  // Compute calculator result. Walk every split newer than the
  // purchase date and accumulate the share-multiplier inverse. We
  // prefer split_to/split_from per event over the cumulative factor
  // because the per-event ratios are the cleanest data and we can
  // also surface which specific splits the user's holding passed
  // through. Reverse splits flip the ratio so the formula is
  // direction-agnostic.
  const calcResult = useMemo(() => {
    if (!splits || !splits.length) return null;
    const sharesNum = parseFloat(oldShares);
    if (!Number.isFinite(sharesNum) || sharesNum <= 0) return null;
    if (!purchaseDate) return null;
    // splits are newest first — process oldest first so the math
    // reads naturally and reverse-split cases compound correctly.
    const applied = [];
    let working = sharesNum;
    const chronological = [...splits].sort((a, b) => (a.execution_date || '').localeCompare(b.execution_date || ''));
    chronological.forEach((s) => {
      if (!s.execution_date) return;
      if (s.execution_date > purchaseDate) {
        const num = Number(s.split_to);
        const den = Number(s.split_from);
        if (num > 0 && den > 0) {
          const mult = num / den;
          working = working * mult;
          applied.push({ ...s, multiplier: mult, runningShares: working });
        }
      }
    });
    return {
      input: sharesNum,
      output: working,
      applied,
      diffPct: applied.length ? ((working - sharesNum) / sharesNum) * 100 : 0,
    };
  }, [splits, oldShares, purchaseDate]);

  // Hide entirely for non-paid users so the card doesn't tease them.
  // Pro tier gating happens server-side via 402.
  if (denied) return null;
  // Hide while loading initially so we don't flash an empty card.
  if (splits === null) return null;
  // Hide when ticker has zero splits on record.
  if (!splits.length) return null;

  return (
    <div className="rounded-lg border bg-card p-3" data-testid="split-history-card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <div className="flex items-center gap-1.5">
          <Split className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" />
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
            Splits History &amp; Cost-Basis Calculator
          </h4>
        </div>
        <span className="text-[10px] font-bold text-muted-foreground">
          {splits.length} split{splits.length === 1 ? '' : 's'} on record
        </span>
      </div>

      {/* Round 96aa+ (Jul 2026) — Post-split 3-month momentum callout.
          Now aggregates the AVERAGE 90-day return across every past
          split on record (capped at 8 events server-side). Uses
          split-adjusted closes so pre/post prices are comparable.
          Self-hides when no measurable events are available. */}
      {momentum?.avg_return_pct != null && momentum?.event_count > 0 && (
        <div
          className={`mb-3 rounded-md border px-3 py-2 text-xs flex flex-wrap items-center gap-2 ${
            momentum.avg_return_pct >= 5
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
              : momentum.avg_return_pct <= -5
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-800 dark:text-rose-200'
                : 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200'
          }`}
          data-testid="post-split-momentum-callout"
        >
          <span className="font-semibold">Post-split average 90-day return:</span>
          <span className="ml-auto font-mono font-bold text-sm" data-testid="post-split-return-pct">
            {momentum.avg_return_pct >= 0 ? '+' : ''}{momentum.avg_return_pct.toFixed(2)}%
          </span>
          <span className="basis-full text-[10px] opacity-80">
            Across {momentum.event_count} past split{momentum.event_count === 1 ? '' : 's'}
            {momentum.positive_events != null && momentum.event_count > 0 ? (
              <> · <span className="font-mono">{momentum.positive_events}/{momentum.event_count}</span> positive</>
            ) : null}
            {momentum.median_return_pct != null ? (
              <> · median <span className="font-mono">{momentum.median_return_pct >= 0 ? '+' : ''}{momentum.median_return_pct.toFixed(2)}%</span></>
            ) : null}
            {momentum.best_return_pct != null && momentum.worst_return_pct != null && momentum.event_count > 1 ? (
              <> · range <span className="font-mono">{momentum.worst_return_pct >= 0 ? '+' : ''}{momentum.worst_return_pct.toFixed(2)}%</span> to <span className="font-mono">{momentum.best_return_pct >= 0 ? '+' : ''}{momentum.best_return_pct.toFixed(2)}%</span></>
            ) : null}
            {momentum.last_split?.display_ratio && momentum.last_split?.execution_date ? (
              <> · latest {momentum.last_split.display_ratio} on <span className="font-mono">{momentum.last_split.execution_date}</span></>
            ) : null}
          </span>
        </div>
      )}

      {/* Split history list */}
      <div className="overflow-x-auto rounded border border-border/60 mb-3">
        <table className="min-w-full text-[11px]" data-testid="split-history-table">
          <thead className="bg-muted/40">
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-2 py-1.5 text-left">Execution Date</th>
              <th className="px-2 py-1.5 text-left">Type</th>
              <th className="px-2 py-1.5 text-center">Ratio</th>
              <th className="px-2 py-1.5 text-right">Share Multiplier</th>
              <th className="px-2 py-1.5 text-right">Cumulative Price Factor</th>
            </tr>
          </thead>
          <tbody>
            {splits.map((s, i) => {
              const num = Number(s.split_to);
              const den = Number(s.split_from);
              const mult = num > 0 && den > 0 ? num / den : null;
              const isReverse = (s.adjustment_type || '').toLowerCase().startsWith('reverse');
              const isFwd = (s.adjustment_type || '').toLowerCase().startsWith('forward');
              const chipCls = isReverse
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300'
                : isFwd
                ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-300'
                : 'bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-300';
              return (
                <tr key={s.execution_date + i} className="border-t border-border/40 hover:bg-muted/20">
                  <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">{fmtDate(s.execution_date)}</td>
                  <td className="px-2 py-1.5">
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${chipCls}`}>
                      {s.adjustment_type || '—'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">
                    {num > 0 && den > 0 ? `${num.toFixed(num % 1 ? 4 : 0)}-for-${den.toFixed(den % 1 ? 4 : 0)}` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold">
                    {mult != null ? `×${mult.toFixed(mult >= 1 ? 2 : 4)}` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-muted-foreground">
                    {s.historical_adjustment_factor != null ? Number(s.historical_adjustment_factor).toFixed(6) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Cost-Basis Calculator */}
      <div className="rounded border border-border/60 bg-muted/20 p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Calculator className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
          <h5 className="text-[11px] font-bold uppercase tracking-widest text-sky-700 dark:text-sky-300">
            Split-Adjusted Cost-Basis Calculator
          </h5>
        </div>
        <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
          Paste an old purchase date plus the share count you bought to see today&apos;s split-adjusted equivalent.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Purchase Date</span>
            <input
              type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              className="w-full px-2 py-1 text-sm rounded border border-border bg-background"
              data-testid="split-calc-date"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Original Share Count</span>
            <input
              type="number"
              min="0"
              step="any"
              placeholder="e.g. 100"
              value={oldShares}
              onChange={(e) => setOldShares(e.target.value)}
              className="w-full px-2 py-1 text-sm rounded border border-border bg-background"
              data-testid="split-calc-shares"
            />
          </label>
        </div>

        {calcResult && (
          <div className="space-y-2" data-testid="split-calc-result">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <span className="text-[11px] text-muted-foreground">Today&apos;s split-adjusted basis</span>
              <span className="text-base font-mono font-bold text-foreground">
                {fmtShares(calcResult.output)} shares
                {calcResult.diffPct !== 0 && (
                  <span className={`ml-2 text-[10px] font-semibold ${calcResult.diffPct > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    ({calcResult.diffPct > 0 ? '+' : ''}{calcResult.diffPct.toFixed(0)}%)
                  </span>
                )}
              </span>
            </div>
            {calcResult.applied.length > 0 ? (
              <div className="text-[10px] text-muted-foreground leading-relaxed">
                <span className="font-semibold">{calcResult.applied.length} split{calcResult.applied.length === 1 ? '' : 's'} applied</span>
                {' — '}
                {calcResult.applied.map((s, i) => (
                  <span key={i}>
                    {fmtDate(s.execution_date)} ×{s.multiplier.toFixed(s.multiplier >= 1 ? 2 : 4)}
                    {i < calcResult.applied.length - 1 ? ' → ' : ''}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-muted-foreground italic">
                Your purchase date is newer than every split on record — no adjustment needed.
              </p>
            )}
          </div>
        )}

        {!calcResult && (oldShares || purchaseDate) && (
          <p className="text-[11px] text-muted-foreground italic">
            Enter both a purchase date and a share count to see the adjusted total.
          </p>
        )}
      </div>
    </div>
  );
}
